import type { MeUser } from "@/types/model/User";
import { PLAN_MODE, SELFSERVICE_PAID_PLANS } from "./constants";
import type { PlanViewConfigMap } from "@/components/routes/feature-access/featureAccess.types";
import { VIEWS } from "@/components/routes/routes";

// Plan-based checkers
export const planIsFreeTrial = (plan: string): boolean => {
  return plan === PLAN_MODE.FREE;
};

// User-based checkers
export const userIsSelfService = (user: MeUser): boolean => {
  return (
    user.planMode === PLAN_MODE.FREE || user.planMode === PLAN_MODE.SELFSERVICE
  );
};

export const userIsSelfServicePaid = (user: MeUser): boolean => {
  return user.planMode === PLAN_MODE.SELFSERVICE;
};

export const userIsAppSumo = (user: MeUser): boolean => {
  return user.planMode === PLAN_MODE.APPSUMO;
};

export const userIsManaged = (user: MeUser): boolean => {
  return user.planMode === PLAN_MODE.MANAGED;
};

export const userIsDemo = (user: MeUser): boolean => {
  return user.planMode === PLAN_MODE.DEMO;
};

export const userIsFreeTrial = (user: MeUser): boolean => {
  return planIsFreeTrial(user.planMode);
};

export const userIsLite = (user: MeUser): boolean => {
  return user.planType === SELFSERVICE_PAID_PLANS.LITE;
};

export const PLAN_VIEW_CONFIG: PlanViewConfigMap = {
  free_trial: {
    // Intelligence - visible
    [VIEWS.OVERVIEW]: "visible",
    [`${VIEWS.OVERVIEW}.trends`]: "locked",
    [`${VIEWS.OVERVIEW}.watchlist`]: "locked",
    [VIEWS.ANALYTICS]: "visible",
    [VIEWS.SENSOR_JS]: "visible",
    [VIEWS.LEADS]: "locked",

    // Intelligence - locked (show GetFeature)
    [VIEWS.PERFORMANCE]: "locked",
    [VIEWS.BENCHMARK]: "locked",
    [VIEWS.REPORTS]: "locked",
    [VIEWS.FORENSICS]: "locked",
    [VIEWS.CONVERSIONS]: "locked",
    [VIEWS.ALERTS]: "locked",

    // Channels - locked
    [VIEWS.GOOGLE.MAIN]: "locked",
    [VIEWS.GOOGLE.SEARCH_DISPLAY]: "hidden",
    [VIEWS.GOOGLE.SHOPPING]: "hidden",
    [VIEWS.GOOGLE.PMAX_DEMANDGEN]: "hidden",
    [VIEWS.GOOGLE.GLOBAL_INTELLIGENCE]: "hidden",
    [VIEWS.GOOGLE.ACCOUNT_SETTINGS]: "hidden",
    [VIEWS.META]: "locked",
    [VIEWS.MICROSOFT]: "locked",
    [VIEWS.TIKTOK]: "locked",
    [VIEWS.HUBSPOT]: "locked",
    [VIEWS.TABOOLA]: "locked",
    [VIEWS.CHANNELS_CENTER]: "locked",

    // Sensor Firewall - hidden (not shown in menu)
    [VIEWS.SENSOR_FIREWALL.CAMPAIGNS]: "hidden",
    [VIEWS.SENSOR_FIREWALL.OFFERS]: "hidden",
    [VIEWS.SENSOR_FIREWALL.TRAFFIC_SOURCES]: "hidden",

    // Security - locked
    [VIEWS.SECURITY.APK]: "hidden",
    [VIEWS.SECURITY.IP_SETTINGS]: "hidden",
    [VIEWS.SECURITY.JS_SETTINGS]: "hidden",
    [VIEWS.SECURITY.POSTBACK]: "hidden",
    [VIEWS.SECURITY.DOMAIN_EXCLUSIONS]: "hidden",

    // Integration - locked
    [VIEWS.SETUP_CENTER]: "visible",
    [VIEWS.INTEGRATIONS_HOME]: "visible",
    [VIEWS.ACCOUNT.SETTINGS]: "visible",
    [VIEWS.ACCOUNT.SUBSCRIPTION_CENTER]: "visible",
    [VIEWS.ACCOUNT.REFERRAL]: "visible",
    [VIEWS.ACCOUNT.USERS]: "visible",
    [VIEWS.ACCOUNT.ACCESS_SETTINGS]: "visible",
  },

  lite: {
    // Intelligence - visible
    [VIEWS.OVERVIEW]: "visible",
    [VIEWS.ANALYTICS]: "visible",
    [VIEWS.ALERTS]: "visible",
    [VIEWS.SENSOR_JS]: "visible",
    [VIEWS.LEADS]: "visible",

    // Intelligence - locked
    [VIEWS.PERFORMANCE]: "locked",
    [VIEWS.BENCHMARK]: "locked",
    [VIEWS.REPORTS]: "locked",
    [VIEWS.FORENSICS]: "locked",
    [VIEWS.CONVERSIONS]: "locked",

    // Channels - locked
    [VIEWS.GOOGLE.MAIN]: "locked",
    [VIEWS.GOOGLE.SEARCH_DISPLAY]: "hidden",
    [VIEWS.GOOGLE.SHOPPING]: "hidden",
    [VIEWS.GOOGLE.PMAX_DEMANDGEN]: "hidden",
    [VIEWS.GOOGLE.GLOBAL_INTELLIGENCE]: "hidden",
    [VIEWS.GOOGLE.ACCOUNT_SETTINGS]: "hidden",
    [VIEWS.META]: "locked",
    [VIEWS.MICROSOFT]: "locked",
    [VIEWS.TIKTOK]: "locked",
    [VIEWS.HUBSPOT]: "locked",
    [VIEWS.TABOOLA]: "locked",
    [VIEWS.CHANNELS_CENTER]: "locked",
    // Sensor Firewall - hidden
    [VIEWS.SENSOR_FIREWALL.CAMPAIGNS]: "hidden",
    [VIEWS.SENSOR_FIREWALL.OFFERS]: "hidden",
    [VIEWS.SENSOR_FIREWALL.TRAFFIC_SOURCES]: "hidden",

    // Security - locked
    [VIEWS.SECURITY.APK]: "hidden",
    [VIEWS.SECURITY.IP_SETTINGS]: "hidden",
    [VIEWS.SECURITY.JS_SETTINGS]: "hidden",
    [VIEWS.SECURITY.POSTBACK]: "hidden",
    [VIEWS.SECURITY.DOMAIN_EXCLUSIONS]: "hidden",

    // Integration - locked
    [VIEWS.SETUP_CENTER]: "visible",
    [VIEWS.INTEGRATIONS_HOME]: "visible",
    [VIEWS.ACCOUNT.SETTINGS]: "visible",
    [VIEWS.ACCOUNT.SUBSCRIPTION_CENTER]: "visible",
    [VIEWS.ACCOUNT.REFERRAL]: "visible",
    [VIEWS.ACCOUNT.USERS]: "visible",
    [VIEWS.ACCOUNT.ACCESS_SETTINGS]: "visible",
  },

  pro: {
    // Intelligence - visible
    [VIEWS.OVERVIEW]: "visible",
    [VIEWS.ANALYTICS]: "visible",
    [VIEWS.PERFORMANCE]: "visible",
    [VIEWS.BENCHMARK]: "visible",
    [VIEWS.FORENSICS]: "visible",
    [VIEWS.CONVERSIONS]: "visible",
    [VIEWS.ALERTS]: "visible",
    [VIEWS.SENSOR_JS]: "visible",
    [VIEWS.LEADS]: "visible",

    // Intelligence - locked
    [VIEWS.REPORTS]: "locked",

    // Channels - visible
    [VIEWS.GOOGLE.MAIN]: "visible",
    [VIEWS.GOOGLE.SEARCH_DISPLAY]: "visible",
    [VIEWS.GOOGLE.SHOPPING]: "visible",
    [VIEWS.GOOGLE.PMAX_DEMANDGEN]: "visible",
    [VIEWS.GOOGLE.GLOBAL_INTELLIGENCE]: "visible",
    [VIEWS.GOOGLE.ACCOUNT_SETTINGS]: "visible",
    [VIEWS.META]: "visible",
    [VIEWS.META_INTEGRATION]: "visible",
    [VIEWS.MICROSOFT]: "visible",
    [VIEWS.MICROSOFT_INTEGRATION]: "visible",
    [VIEWS.TIKTOK]: "visible",
    [VIEWS.TIKTOK_INTEGRATION]: "visible",
    [VIEWS.TABOOLA]: "visible",
    [VIEWS.TABOOLA_INTEGRATION]: "visible",
    [VIEWS.HUBSPOT]: "visible",
    [VIEWS.CHANNELS_CENTER]: "visible",

    // Sensor Firewall - hidden
    [VIEWS.SENSOR_FIREWALL.CAMPAIGNS]: "hidden",
    [VIEWS.SENSOR_FIREWALL.OFFERS]: "hidden",
    [VIEWS.SENSOR_FIREWALL.TRAFFIC_SOURCES]: "hidden",

    // Security - visible
    [VIEWS.SECURITY.APK]: "visible",
    [VIEWS.SECURITY.IP_SETTINGS]: "visible",
    [VIEWS.SECURITY.JS_SETTINGS]: "visible",
    [VIEWS.SECURITY.POSTBACK]: "visible",
    [VIEWS.SECURITY.DOMAIN_EXCLUSIONS]: "visible",

    // Integration - locked
    [VIEWS.SETUP_CENTER]: "visible",
    [VIEWS.INTEGRATIONS_HOME]: "visible",
    [VIEWS.ACCOUNT.SETTINGS]: "visible",
    [VIEWS.ACCOUNT.SUBSCRIPTION_CENTER]: "visible",
    [VIEWS.ACCOUNT.REFERRAL]: "visible",
    [VIEWS.ACCOUNT.USERS]: "visible",
    [VIEWS.ACCOUNT.ACCESS_SETTINGS]: "visible",
  },

  max: {
    // Intelligence - visible
    [VIEWS.OVERVIEW]: "visible",
    [VIEWS.ANALYTICS]: "visible",
    [VIEWS.PERFORMANCE]: "visible",
    [VIEWS.BENCHMARK]: "visible",
    [VIEWS.REPORTS]: "visible",
    [VIEWS.FORENSICS]: "visible",
    [VIEWS.CONVERSIONS]: "visible",
    [VIEWS.ALERTS]: "visible",
    [VIEWS.SENSOR_JS]: "visible",
    [VIEWS.LEADS]: "visible",

    // Channels - visible
    [VIEWS.GOOGLE.MAIN]: "visible",
    [VIEWS.GOOGLE.SEARCH_DISPLAY]: "visible",
    [VIEWS.GOOGLE.SHOPPING]: "visible",
    [VIEWS.GOOGLE.PMAX_DEMANDGEN]: "visible",
    [VIEWS.GOOGLE.GLOBAL_INTELLIGENCE]: "visible",
    [VIEWS.GOOGLE.ACCOUNT_SETTINGS]: "visible",
    [VIEWS.META]: "visible",
    [VIEWS.META_INTEGRATION]: "visible",
    [VIEWS.MICROSOFT]: "visible",
    [VIEWS.MICROSOFT_INTEGRATION]: "visible",
    [VIEWS.TIKTOK]: "visible",
    [VIEWS.TIKTOK_INTEGRATION]: "visible",
    [VIEWS.HUBSPOT]: "visible",
    [VIEWS.TABOOLA]: "visible",
    [VIEWS.TABOOLA_INTEGRATION]: "visible",
    [VIEWS.CHANNELS_CENTER]: "visible",
    // Sensor Firewall - hidden
    [VIEWS.SENSOR_FIREWALL.CAMPAIGNS]: "hidden",
    [VIEWS.SENSOR_FIREWALL.OFFERS]: "hidden",
    [VIEWS.SENSOR_FIREWALL.TRAFFIC_SOURCES]: "hidden",

    // Security - visible
    [VIEWS.SECURITY.APK]: "visible",
    [VIEWS.SECURITY.IP_SETTINGS]: "visible",
    [VIEWS.SECURITY.JS_SETTINGS]: "visible",
    [VIEWS.SECURITY.POSTBACK]: "visible",
    [VIEWS.SECURITY.DOMAIN_EXCLUSIONS]: "visible",

    // Integration - locked
    [VIEWS.SETUP_CENTER]: "visible",
    [VIEWS.INTEGRATIONS_HOME]: "visible",
    [VIEWS.ACCOUNT.SETTINGS]: "visible",
    [VIEWS.ACCOUNT.SUBSCRIPTION_CENTER]: "visible",
    [VIEWS.ACCOUNT.REFERRAL]: "visible",
    [VIEWS.ACCOUNT.USERS]: "visible",
    [VIEWS.ACCOUNT.ACCESS_SETTINGS]: "visible",
    [VIEWS.ACCOUNT.DEVELOPERS_SETTINGS]: "visible",
  },
};
